import { Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import UsersApi from "../../../api/UsersApi";
import { appConstants } from "../../../config/app-constants";
import { EToastvariant, IAddEditUser, IUserForm, IUserResponse } from "../../../config/interfaces";
import { useAppDispatch } from "../../../redux/Store";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import ScrollToFirstError from "../../ScrollToFirstError";
import PhoneInputField from "../../fields/PhoneInputField";
import { ButtonSpinner } from "../../placeholders";

const INIT_UF_VALUES: IUserForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  inactive: false
};

const INITIAL_FORM_SCHEMA = {
  firstName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "First Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid First Name")
    .required("First Name is required"),
  middleName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Middle Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid Middle Name")
    .notRequired(),
  lastName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Last Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid Last Name")
    .required("Last Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .trim()
    .matches(appConstants.EMAIL_REGEX, "Invalid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .trim()
    .min(6, "Invalid phoneNumber number")
    .max(15, "Invalid phoneNumber number")
    .notRequired(),
  inactive: Yup.boolean()
};

type Props = {
  pageName?: string;
};

const AdminUserProfile: React.FC<Props> = (props: Props) => {
  const { pageName } = props;

  const { uUId } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [initialFormValues, setInitialFormValues] = useState(INIT_UF_VALUES);
  const [initialFormSchema, setInitialFormSchema] = useState(
    Yup.object().shape(INITIAL_FORM_SCHEMA)
  );

  useEffect(() => {
    getUserDetails();
  }, []);

  const getUserDetails = async () => {
    try {
      if (!uUId) {
        return;
      }
      const adminUsersApi = UsersApi.getInstance();
      const UserResp = await adminUsersApi.getUserById(uUId);
      const _data = UserResp;
      const _userData = {
        firstName: _data?.firstName || "",
        middleName: _data?.middleName || "",
        lastName: _data?.lastName || "",
        email: _data?.email || "",
        phoneNumber: _data?.phoneNumber || "",
        inactive: _data?.inactive || false
      };
      setInitialFormValues(() => _userData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  const handleAddEditFormSubmit = async (
    values: IUserForm,
    { setSubmitting, resetForm }: FormikHelpers<IUserForm>
  ) => {
    try {
      setSubmitting(true);
      const fullName = `${values?.firstName || ""} ${values?.lastName || ""}`;
      let _msg = `${fullName} added successfully`;
      let _uUId = uUId;
      const _values: IAddEditUser = {
        firstName: values?.firstName || "",
        middleName: values?.middleName || "",
        lastName: values?.lastName || "",
        phoneNumber: values?.phoneNumber || "",
        inactive: values?.inactive || false
      };
      let responseData: IUserResponse | null = null;
      const adminUsersApi = UsersApi.getInstance();
      if (_uUId) {
        responseData = await adminUsersApi.updateUserById(_uUId, _values);
        _msg = `Changes applied to ${fullName} successfully`;
      } else {
        responseData = await adminUsersApi.addUser(_values);
        _uUId = responseData.uuid;
        resetForm();
      }
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
      navigate(`/${pageName}`);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="form-card">
        <Card.Body>
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={initialFormSchema}
            onSubmit={handleAddEditFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              validateForm,
              values,
              isValid,
              errors,
              touched,
              submitCount,
              isSubmitting,
              setTouched
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Row className="align-items-center">
                    <Col sm={3}>
                      <Form.Label className="mb-0">Name</Form.Label>
                    </Col>
                    <Col>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Group className="form-group" controlId="firstName">
                            <Form.Label className="small-label required-field">
                              First Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder="First Name"
                              maxLength={255}
                              name="firstName"
                              value={values.firstName}
                              onBlur={handleBlur("firstName")}
                              onChange={handleChange("firstName")}
                              isInvalid={Boolean(errors.firstName) && touched.firstName}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.firstName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col sm={4}>
                          <Form.Group className="form-group" controlId="middleName">
                            <Form.Label className="small-label">Middle Name</Form.Label>
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder="Middle Name"
                              maxLength={255}
                              name="middleName"
                              value={values.middleName}
                              onBlur={handleBlur("middleName")}
                              onChange={handleChange("middleName")}
                              isInvalid={Boolean(errors.middleName) && touched.middleName}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.middleName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col sm={4}>
                          <Form.Group className="form-group" controlId="lastName">
                            <Form.Label className="small-label required-field">
                              Last Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder="Last Name"
                              maxLength={255}
                              name="lastName"
                              value={values.lastName}
                              onBlur={handleBlur("lastName")}
                              onChange={handleChange("lastName")}
                              isInvalid={Boolean(errors.lastName) && touched.lastName}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.lastName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="align-items-center">
                    <Col sm={3}>
                      <Form.Label className="required-field inline-label">Email</Form.Label>
                    </Col>
                    <Col>
                      <Form.Group className="form-group" controlId="email">
                        <Form.Control
                          type="text"
                          autoComplete="false"
                          placeholder="Email"
                          maxLength={255}
                          name="email"
                          value={values.email}
                          onBlur={handleBlur("email")}
                          onChange={handleChange}
                          isInvalid={Boolean(errors.email) && touched.email}
                          readOnly={true}
                        />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-center">
                    <Col sm={3}>
                      <Form.Label className="inline-label">Phone</Form.Label>
                    </Col>
                    <Col>
                      <PhoneInputField
                        name="phoneNumber"
                        value={values.phoneNumber}
                        onChange={(value) => {
                          setFieldValue("phoneNumber", value);
                        }}
                        onBlur={() => {
                          handleBlur("phoneNumber");
                        }}
                        isInvalid={Boolean(errors.phoneNumber) && touched.phoneNumber}
                        errorMsg={errors.phoneNumber}
                        formType="GENERAL"
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-center">
                    <Col sm={3}>
                      <Form.Label className="required-field inline-label">Status</Form.Label>
                    </Col>
                    <Col>
                      <ToggleButtonGroup
                        type="radio"
                        name="inactive"
                        value={values.inactive ? "Inactive" : "Active"}
                        onChange={(selectValue) => {
                          const newValue = selectValue === "Inactive"; // Convert "Inactive" to true, "Active" to false
                          setFieldValue("inactive", newValue);
                        }}
                        className="rb-group-control"
                      >
                        <ToggleButton
                          variant="outline-success"
                          id="active"
                          value="Active"
                          className="btn-w-110 btn-h-40"
                        >
                          Active
                        </ToggleButton>
                        <ToggleButton
                          variant="outline-danger"
                          id="inactive"
                          value="Inactive"
                          className="btn-w-110 btn-h-40"
                        >
                          Inactive
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Col>
                  </Row>

                  <div className="form-btns-wrap">
                    <Button
                      size="lg"
                      variant="outline-greyDark"
                      className="btn-w-120"
                      onClick={() => {
                        uUId ? navigate(`/${pageName}`) : navigate(-1);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="lg"
                      variant="primary"
                      className="btn-w-160"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <ButtonSpinner /> : "Save"}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export default AdminUserProfile;
